/**
 * useHasAccounts Hook
 *
 * Lightweight hook to check if the user has any accounts.
 * Uses the same underlying queries as other hooks for cache efficiency.
 * Waits for authentication before making API calls.
 */

import { useAuth } from '../auth/AuthContext';
import { useActivePortfolio } from './useActivePortfolio';
import { useAccountsView } from './usePortfolios';

interface UseHasAccountsResult {
  hasAccounts: boolean;
  isLoading: boolean;
  accountCount: number;
}

export function useHasAccounts(): UseHasAccountsResult {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Only fetch portfolios if authenticated and not loading
  const { activePortfolio, portfolioId, isLoading: loadingPortfolios } = useActivePortfolio({
    enabled: isAuthenticated && !authLoading,
  });

  const { data: accounts = [], isLoading: loadingAccounts } = useAccountsView(
    portfolioId,
    { enabled: !!activePortfolio && isAuthenticated }
  );

  return {
    hasAccounts: accounts.length > 0,
    isLoading: authLoading || loadingPortfolios || loadingAccounts,
    accountCount: accounts.length,
  };
}
